<template>
    <!-- "Who signs this document?" (RAC-10324). Patterned on TenantRosterStep but
         deliberately NOT a reuse of it: that component is an editable-length
         roster, while a document's signature blocks are fixed by the template —
         the landlord fills in who each block belongs to, and can neither add nor
         remove one. -->
    <div class="bg-white p-6">
        <h3 class="text-lg font-semibold text-gray-800 mb-1">Who signs this {{ documentName }}?</h3>
        <p class="text-sm text-gray-500 mb-4">
            This document has {{ signerRows.length }} signature {{ signerRows.length === 1 ? 'block' : 'blocks' }}.
            Everyone is emailed their own secure signing link at the same time, so each one needs a different
            email address.
        </p>

        <form class="space-y-4" @submit.prevent="handleSubmit">
            <fieldset
                v-for="(signer, idx) in signerRows"
                :key="signer.id"
                class="border border-gray-200 rounded-lg p-4"
            >
                <legend class="text-sm font-medium text-gray-800 px-1">{{ signer.role_label }}</legend>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label :for="`signer-name-${signer.id}`" class="block text-sm text-gray-700 mb-1">
                            Full name <span class="text-red-500">*</span>
                        </label>
                        <!-- This is the RocketLawyer party name, NOT the name printed on the
                             signature line — the document renders whatever the interview
                             collected. Verified against an executed Move Out Inspection
                             Checklist whose lines read the interview names while the parties
                             carried the ones typed here. -->
                        <input
                            :id="`signer-name-${signer.id}`"
                            v-model="signer.name"
                            type="text"
                            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                        >
                        <p v-if="signer.name_is_placeholder && !errors[idx]?.name" class="text-xs text-gray-500 mt-1">
                            Identifies this signer during signing. The name printed on the document
                            itself comes from the answers given in the wizard, not from here.
                        </p>
                        <p v-if="errors[idx]?.name" class="text-xs text-red-600 mt-1">{{ errors[idx].name }}</p>
                    </div>

                    <div>
                        <label :for="`signer-email-${signer.id}`" class="block text-sm text-gray-700 mb-1">
                            Email <span class="text-red-500">*</span>
                        </label>
                        <input
                            :id="`signer-email-${signer.id}`"
                            v-model="signer.email"
                            type="email"
                            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                        >
                        <p v-if="errors[idx]?.email" class="text-xs text-red-600 mt-1">{{ errors[idx].email }}</p>
                    </div>
                </div>
            </fieldset>

            <p v-if="serverError" class="text-sm text-red-600 bg-red-50 p-2 rounded">{{ serverError }}</p>

            <div class="flex flex-col sm:flex-row justify-between gap-3 pt-4 border-t border-gray-100">
                <button type="button" class="closeButton" @click="$emit('cancel')">Cancel</button>
                <button type="submit" class="primaryButton" :disabled="busy" :class="{ 'opacity-25': busy }">
                    {{ busy ? busyLabel : 'Send for Signature' }}
                </button>
            </div>
        </form>
    </div>
</template>

<script setup>
import { reactive, ref } from 'vue'

const props = defineProps({
    documentName: { type: String, default: 'document' },
    // One entry per signature block on the generated document, from the binder.
    signers: { type: Array, default: () => [] },
    busy: { type: Boolean, default: false },
    busyLabel: { type: String, default: 'Sending…' },
    serverError: { type: String, default: null },
})

const emit = defineEmits(['submit', 'cancel'])

const signerRows = reactive(
    (props.signers || []).map((signer) => ({
        id: signer.id,
        role_label: signer.role_label || 'Signer',
        name: signer.name || '',
        email: signer.email || '',
        name_is_placeholder: signer.name_is_placeholder === true,
    })),
)

const errors = ref([])

const validate = () => {
    const found = []
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const seen = new Map()

    signerRows.forEach((signer, idx) => {
        const rowErrors = {}
        if (!signer.name.trim()) {
            rowErrors.name = 'Name is required — it identifies this signer during signing.'
        }

        const email = signer.email.trim().toLowerCase()
        if (!email) {
            rowErrors.email = 'Email is required — this person signs electronically.'
        } else if (!emailRe.test(email)) {
            rowErrors.email = 'Enter a valid email address.'
        } else if (seen.has(email)) {
            // RocketLawyer rejects a binder whose parties share an address, and
            // catching it here beats surfacing its raw rejection after the send.
            rowErrors.email = 'Each signer needs a different email address.'
        } else {
            seen.set(email, idx)
        }

        if (Object.keys(rowErrors).length > 0) {
            found[idx] = rowErrors
        }
    })

    errors.value = found
    return found.length === 0
}

const handleSubmit = () => {
    if (!validate()) {
        return
    }

    emit(
        'submit',
        signerRows.map((signer) => ({
            id: signer.id,
            name: signer.name.trim(),
            email: signer.email.trim(),
        })),
    )
}
</script>
